import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ShieldCheck, Sparkles, ChevronRight, ArrowRight, Info } from 'lucide-react';
import {
  DOOR_MATERIALS, VENEER_SPECIES, MICA_PALETTE, PU_PAINT_PALETTE,
  MATERIAL_CARE, veneerGrainSvg,
} from '../lib/designStudio';

/**
 * Reusable SEO landing page for a material family.
 * Optional `cncFocus` — when true, positions the page as "CNC-focused" (for mica-cnc, veneer-cnc routes).
 */
const MaterialLanding = ({ materialId, cncFocus = false }) => {
  const material = DOOR_MATERIALS.find((m) => m.id === materialId);
  if (!material) return null;

  const title = cncFocus
    ? `${material.name.split(' ')[0]} CNC Doors`
    : material.name.replace('/ Decorative Laminate', '').replace('(Nail-Free)', '').trim() + ' Doors';

  const heroSubtitle = cncFocus
    ? `Precision CNC patterns milled into ${material.name} — bonded with CriLam's PUR nail-free technology.`
    : material.long;

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid={`landing-${materialId}${cncFocus ? '-cnc' : ''}`}>
      {/* Hero */}
      <section className={`relative bg-[#0A0D0C] pt-32 pb-16 overflow-hidden`}>
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className={`absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l ${material.accentGradient} opacity-25`} />
        <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-sm font-bold mb-6">
              <Sparkles className="h-4 w-4" /> {material.tag.toUpperCase()} · CRILAM PUR TECHNOLOGY
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Outfit'] leading-tight">
              {title}
              <span className="block text-[#D4AF37] text-2xl md:text-3xl mt-2 font-light">Nail-Free · PUR-Bonded · Made in Kanpur</span>
            </h1>
            <p className="text-lg text-white/80 max-w-3xl mb-6">{heroSubtitle}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/door-designer">
                <button className="px-6 py-3 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                  Design Your {material.name.split(' ')[0]} Door <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link to="/contact">
                <button className="px-6 py-3 border-2 border-white/40 text-white font-medium rounded-lg hover:bg-white/10">
                  Get Quote
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features grid */}
      <section className="py-16">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#004534] mb-4 font-['Outfit']">Why {material.name.split(' ')[0]}?</h2>
              <p className="text-gray-600 mb-6">{material.long}</p>
              <ul className="space-y-3">
                {material.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#004534] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="h-3.5 w-3.5 text-white" />
                    </div>
                    <span className="text-sm text-gray-700 font-medium">{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex items-start gap-3 p-4 bg-[#D4AF37]/10 rounded-lg">
                <ShieldCheck className="h-5 w-5 text-[#7A6621] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-[#7A6621]">CriLam PUR-Bonded · Absolutely Nail-Free</p>
                  <p className="text-xs text-gray-600 mt-1">
                    Every CriLam door uses our proprietary CriLam PUR-Bond™ technology — no visible nails, no edge lifting, no bubbles. Ever.{' '}
                    <Link to="/nail-free-advantage" className="text-[#004534] underline underline-offset-2">Why it matters →</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* Visual grid */}
            <div className={`aspect-[4/5] rounded-2xl overflow-hidden relative bg-gradient-to-br ${material.accentGradient} border ${material.accentBorder} p-6`}>
              {materialId === 'veneer' && (
                <div className="grid grid-cols-2 gap-2 h-full">
                  {VENEER_SPECIES.slice(0, 6).map((v) => (
                    <div key={v.id} className="rounded-lg overflow-hidden shadow-md">
                      <div className="aspect-square">{veneerGrainSvg(v.id)}</div>
                      <div className="p-1.5 bg-white text-center">
                        <p className="text-[10px] font-bold text-[#004534] truncate">{v.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {materialId === 'mica' && (
                <div className="grid grid-cols-4 gap-2 h-full auto-rows-fr">
                  {MICA_PALETTE.slice(0, 16).map((m) => (
                    <div key={m.id} className="rounded-lg shadow-md flex flex-col overflow-hidden">
                      <div className="flex-1" style={{ backgroundColor: m.hex }} />
                      <div className="bg-white text-center py-1">
                        <p className="text-[8px] font-bold text-[#004534] truncate px-1">{m.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {materialId === 'pu-paint' && (
                <div className="grid grid-cols-4 gap-2 h-full">
                  {PU_PAINT_PALETTE.slice(0, 16).map((p) => (
                    <div key={p.id} className="rounded-lg shadow-md" style={{ backgroundColor: p.hex }} title={p.name} />
                  ))}
                </div>
              )}
              {materialId === 'acrylic-laminate' && (
                <div className="h-full flex items-center justify-center text-center">
                  <div>
                    <Sparkles className="h-16 w-16 text-[#D4AF37] mx-auto mb-4" />
                    <p className="text-2xl font-bold text-[#004534]">6H</p>
                    <p className="text-sm text-[#7A6621] font-semibold">Surface Hardness</p>
                    <p className="text-xs text-gray-600 mt-3">Anti-Yellowing · UV-Stable<br />100% Waterproof · Mirror Finish</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Care Instructions */}
      <section className="py-12 bg-white border-y">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <h3 className="text-2xl font-bold text-[#004534] mb-4 font-['Outfit']">Caring for Your {material.name.split(' ')[0]} Door</h3>
          <p className="text-gray-600 mb-6">Simple daily care preserves the finish for a lifetime.</p>
          <ul className="grid md:grid-cols-3 gap-4">
            {(MATERIAL_CARE[materialId] || []).map((c, i) => (
              <li key={i} className="p-4 bg-[#F7F8F5] rounded-lg border-l-4 border-[#D4AF37]">
                <div className="text-xs text-[#7A6621] font-bold mb-1">Care Tip {i + 1}</div>
                <p className="text-sm text-gray-700">{c}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Best For rooms */}
      <section className="py-12">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <h3 className="text-2xl font-bold text-[#004534] mb-4 font-['Outfit']">Best For These Rooms</h3>
          <div className="flex flex-wrap gap-2">
            {material.bestFor.map((r) => (
              <span key={r} className="px-4 py-2 bg-[#004534]/10 text-[#004534] font-medium text-sm rounded-full capitalize">
                ✓ {r.replace('-', ' ')}
              </span>
            ))}
          </div>
          {cncFocus && material.cncWarning && (
            <div className="mt-4 flex items-start gap-2 text-sm p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{material.cncWarning}</span>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-br from-[#004534] to-[#0A0D0C] text-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-4 font-['Outfit']">Ready to Design Your {material.name.split(' ')[0]} Door?</h3>
          <p className="text-white/70 mb-8">Our AI Design Studio renders it in seconds. Share with family on WhatsApp. Download branded PDF spec sheet. Get a real quote.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/door-designer">
              <button className="px-8 py-4 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                Start Designing <ChevronRight className="h-5 w-5" />
              </button>
            </Link>
            <Link to="/match-my-doors">
              <button className="px-8 py-4 border-2 border-white/40 text-white font-medium rounded-lg hover:bg-white/10 transition-colors">
                Match My Existing Doors (AI)
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MaterialLanding;
